import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage } from '@kyruus/intl';
import Button from '@kyruus/button';

import { ExpandableCard, ExpandableContainer } from './styles';
import { TrackingFunction, trackShowMoreClick } from '../../lib/tracking-utils';
import messages from '../../lib/messages';

export interface ExpandableCardContainerProps {
  /** The id attribute for the expandable section (required for accessibility) */
  id: string;
  /** The children to display */
  children?: React.ReactNode;
  track: TrackingFunction;
}

const ExpandableCardContainer = ({
  id,
  children,
  track
}: ExpandableCardContainerProps): React.ReactElement => {
  // height of the content that the expandable container is wrapping, which
  // can change (e.g. filtering provider insurances accepted)
  const [contentHeight, setContentHeight] = useState(0);

  // target height of the expandable container
  const [containerHeight, setContainerHeight] = useState(0);

  // boolean that tracks if the content exceeds 600px
  const [isExpandable, setIsExpandable] = useState(false);

  // the HTML elements that need to be removed from the tab index when collapsed
  const [focusableElementsToHide, setFocusableElementsToHide] = useState<
    Array<HTMLElement>
  >([]);

  const content = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  // listen for resize events
  useEffect(() => {
    if (!content.current) return;
    if (typeof window !== 'undefined' && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        setContentHeight(content.current?.offsetHeight || 0);
      });
      resizeObserver.observe(content.current);
      return () => resizeObserver.disconnect();
    }
    return;
  }, []);

  useEffect(() => {
    if (contentHeight > 600) {
      setContainerHeight(600);
      setIsExpandable(true);

      // get all the focusable elements in the content container
      const focusableElements = content.current?.querySelectorAll<HTMLElement>(
        'a[href], button:enabled'
      );

      // build an array of all the elements that need to be hidden when collapsed, based
      // on offsetTop of the elements and the container. only going to hide the ones that
      // are below the fold of the collapsed container (not visible, for accessibility)
      const elementsToHide: Array<HTMLElement> = [];

      setTimeout(() => {
        focusableElements?.forEach((element: HTMLElement) => {
          const tabIndex = parseInt(element.getAttribute('tabindex') || '0');

          if (
            container.current &&
            element.getBoundingClientRect().top >
              container.current.getBoundingClientRect().bottom &&
            tabIndex >= 0
          ) {
            // if the top of the element is below the fold of the collapsed container,
            // add it to the array and remove it from the tab index
            elementsToHide.push(element);
            element.tabIndex = -1;
          }
        });
      }, 10);
      setFocusableElementsToHide(elementsToHide);
    } else {
      setIsExpandable(false);
      setContainerHeight(0);
    }
  }, [contentHeight]);

  const handleButtonClick = () => {
    if (containerHeight === contentHeight) {
      // show less click (collapse)
      trackShowMoreClick(track, 'hide', id);
      setContainerHeight(600);
      setIsExpandable(true);

      // if we're collapsing, hide the focusable elements, collapse the container and
      // scroll the button (which is focused) into view
      focusableElementsToHide.forEach((element) => {
        element.tabIndex = -1;
      });
      setTimeout(() => {
        const buttonIsOffscreen =
          (button.current?.getBoundingClientRect().y || 0) < 0;
        if (buttonIsOffscreen) {
          button.current?.scrollIntoView();
        }
      }, 10);
    } else {
      // show more click (EXPAND!)
      trackShowMoreClick(track, 'show', id);
      setContainerHeight(contentHeight);
      setIsExpandable(false);

      // if we're expanding, add the hidden elements back into the tab index
      setTimeout(() => {
        focusableElementsToHide.forEach((element) => {
          if (container.current) {
            if (
              container.current.getBoundingClientRect().bottom >
              element.getBoundingClientRect().top
            ) {
              element.tabIndex = 0;
            }
          } else {
            element.tabIndex = 0;
          }
        });
      }, 10);
    }
  };

  return (
    <ExpandableCard data-testid="ExpandableCard">
      <ExpandableContainer
        data-testid="ExpandableContainer"
        id={id}
        containerHeight={containerHeight}
        isExpandable={isExpandable}
        ref={container}
      >
        <div ref={content}>{children}</div>
      </ExpandableContainer>
      {contentHeight > 600 && (
        <Button
          className="show-more-button"
          mode="secondary"
          onClick={handleButtonClick}
          ref={button}
          aria-expanded={!isExpandable}
          aria-controls={id}
        >
          {isExpandable ? (
            <FormattedMessage {...messages.reviewsShowMore} />
          ) : (
            <FormattedMessage {...messages.reviewsShowLess} />
          )}
        </Button>
      )}
    </ExpandableCard>
  );
};

export default ExpandableCardContainer;
